import { conversationMessagesResponseSchema } from "@/features/chat/conversationSchemas";
import {
  conversationTurnSeekResponseSchema,
  experienceStrategyCurrentResponseSchema,
  experienceStrategyDetailResponseSchema,
  experienceStrategyMutationResponseSchema,
  experienceStrategyProposalResponseSchema,
  experienceStrategyProposalStatusResponseSchema,
  experienceStrategyTimelineResponseSchema,
} from "@/features/experience-strategy/experienceStrategySchemas";
import type { ExperienceStrategyTimelineQueryArgs } from "@/features/experience-strategy/experienceStrategyQuery";
import { baseApi } from "@/shared/api/baseApi";

interface ExperienceStrategyScopeQuery {
  organizationId: string;
  shopBindingId?: string;
}

interface ExperienceStrategyDetailQuery extends ExperienceStrategyScopeQuery {
  strategyId: string;
  versionId: string;
}

interface ExperienceStrategyProposalQuery {
  organizationId: string;
  proposalId: string;
}

interface ExperienceStrategyProposalMutation extends ExperienceStrategyProposalQuery {
  action: "adjust" | "confirm";
  baseVersionId: string;
  expectedScopeRevision: string;
  expectedStrategyRevision: string;
  idempotencyKey: string;
  proposalHash: string;
  proposalRevision: string;
  strategyId: string;
}

interface RejectExperienceStrategyProposalInput extends ExperienceStrategyProposalQuery {
  expectedRevision: string;
  proposalHash: string;
}

interface ConversationTurnSeekQuery {
  conversationId: string;
  organizationId: string;
  sequence: number;
  shopBindingId: string;
}

interface ConversationMessageWindowQuery {
  beforeSequence: number;
  conversationId: string;
  organizationId: string;
  shopBindingId: string;
}

function organizationPath(organizationId: string) {
  return `/v1/organizations/${encodeURIComponent(organizationId)}`;
}

function experienceStrategiesPath(organizationId: string) {
  return `${organizationPath(organizationId)}/experience-strategies`;
}

function scopeParams(shopBindingId?: string) {
  return shopBindingId
    ? { scope: "shop", shop_binding_id: shopBindingId }
    : { scope: "organization" };
}

function scopeCacheKey(input: ExperienceStrategyScopeQuery) {
  return `${input.organizationId}:${input.shopBindingId ?? "organization"}`;
}

export const experienceStrategyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    confirmExperienceStrategyProposal: builder.mutation<
      ReturnType<typeof experienceStrategyMutationResponseSchema.parse>,
      ExperienceStrategyProposalMutation
    >({
      invalidatesTags: (result, _error, input) => [
        { id: input.proposalId, type: "ExperienceStrategyProposal" },
        { id: input.organizationId, type: "ExperienceStrategyTimeline" },
        { id: input.organizationId, type: "ExperienceStrategyCurrent" },
        ...(result
          ? [
              {
                id: `${result.strategy.strategy_id}:${result.strategy.id}`,
                type: "ExperienceStrategyDetail" as const,
              },
            ]
          : []),
      ],
      query: (input) => ({
        body: {
          ...(input.action === "adjust"
            ? {
                base_version_id: input.baseVersionId,
                expected_strategy_revision: input.expectedStrategyRevision,
              }
            : {}),
          confirmation_channel: "button",
          expected_scope_revision: input.expectedScopeRevision,
          proposal_hash: input.proposalHash,
          proposal_id: input.proposalId,
          proposal_revision: input.proposalRevision,
        },
        headers: { "Idempotency-Key": input.idempotencyKey },
        method: "POST",
        url:
          input.action === "adjust"
            ? `${experienceStrategiesPath(input.organizationId)}/${encodeURIComponent(input.strategyId)}/versions`
            : `${experienceStrategiesPath(input.organizationId)}/confirm`,
      }),
      transformResponse: (response) =>
        experienceStrategyMutationResponseSchema.parse(response),
    }),
    getConversationMessageWindow: builder.query<
      ReturnType<typeof conversationMessagesResponseSchema.parse>,
      ConversationMessageWindowQuery
    >({
      keepUnusedDataFor: 0,
      query: ({ beforeSequence, conversationId, organizationId, shopBindingId }) => ({
        params: { before_sequence: beforeSequence, limit: 100, shop_binding_id: shopBindingId },
        url: `${organizationPath(organizationId)}/conversations/${encodeURIComponent(conversationId)}/messages`,
      }),
      transformResponse: (response) =>
        conversationMessagesResponseSchema.parse(response),
    }),
    getConversationTurnSeek: builder.query<
      ReturnType<typeof conversationTurnSeekResponseSchema.parse>,
      ConversationTurnSeekQuery
    >({
      keepUnusedDataFor: 0,
      query: ({ conversationId, organizationId, sequence, shopBindingId }) => ({
        params: { sequence, shop_binding_id: shopBindingId },
        url: `${organizationPath(organizationId)}/conversations/${encodeURIComponent(conversationId)}/turns/seek`,
      }),
      transformResponse: (response) =>
        conversationTurnSeekResponseSchema.parse(response),
    }),
    getExperienceStrategyCurrent: builder.query<
      ReturnType<typeof experienceStrategyCurrentResponseSchema.parse>,
      ExperienceStrategyScopeQuery
    >({
      providesTags: (_result, _error, input) => [
        { id: scopeCacheKey(input), type: "ExperienceStrategyCurrent" },
        { id: input.organizationId, type: "ExperienceStrategyCurrent" },
      ],
      query: ({ organizationId, shopBindingId }) => ({
        params: scopeParams(shopBindingId),
        url: `${experienceStrategiesPath(organizationId)}/current`,
      }),
      transformResponse: (response) =>
        experienceStrategyCurrentResponseSchema.parse(response),
    }),
    getExperienceStrategyDetail: builder.query<
      ReturnType<typeof experienceStrategyDetailResponseSchema.parse>,
      ExperienceStrategyDetailQuery
    >({
      providesTags: (_result, _error, input) => [
        {
          id: `${input.strategyId}:${input.versionId}`,
          type: "ExperienceStrategyDetail",
        },
      ],
      query: ({ organizationId, shopBindingId, strategyId, versionId }) => ({
        params: scopeParams(shopBindingId),
        url: `${experienceStrategiesPath(organizationId)}/${encodeURIComponent(strategyId)}/versions/${encodeURIComponent(versionId)}`,
      }),
      transformResponse: (response) =>
        experienceStrategyDetailResponseSchema.parse(response),
    }),
    getExperienceStrategyProposal: builder.query<
      ReturnType<typeof experienceStrategyProposalResponseSchema.parse>,
      ExperienceStrategyProposalQuery
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.proposalId, type: "ExperienceStrategyProposal" },
      ],
      query: ({ organizationId, proposalId }) =>
        `${organizationPath(organizationId)}/experience-strategy-proposals/${encodeURIComponent(proposalId)}`,
      transformResponse: (response) =>
        experienceStrategyProposalResponseSchema.parse(response),
    }),
    getExperienceStrategyTimeline: builder.infiniteQuery<
      ReturnType<typeof experienceStrategyTimelineResponseSchema.parse>,
      ExperienceStrategyTimelineQueryArgs,
      string
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
        initialPageParam: "",
      },
      providesTags: (_result, _error, input) => [
        { id: scopeCacheKey(input), type: "ExperienceStrategyTimeline" },
        { id: input.organizationId, type: "ExperienceStrategyTimeline" },
      ],
      query: ({ pageParam, queryArg }) => ({
        params: {
          ...scopeParams(queryArg.shopBindingId),
          ...(queryArg.dateBefore ? { date_before: queryArg.dateBefore } : {}),
          ...(queryArg.dateFrom ? { date_from: queryArg.dateFrom } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(queryArg.search ? { search: queryArg.search } : {}),
          limit: 20,
        },
        url: `${experienceStrategiesPath(queryArg.organizationId)}/timeline`,
      }),
      transformResponse: (response) =>
        experienceStrategyTimelineResponseSchema.parse(response),
    }),
    rejectExperienceStrategyProposal: builder.mutation<
      ReturnType<typeof experienceStrategyProposalStatusResponseSchema.parse>,
      RejectExperienceStrategyProposalInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.proposalId, type: "ExperienceStrategyProposal" },
      ],
      query: ({
        expectedRevision,
        organizationId,
        proposalHash,
        proposalId,
      }) => ({
        body: {
          expected_revision: expectedRevision,
          proposal_hash: proposalHash,
        },
        method: "POST",
        url: `${organizationPath(organizationId)}/experience-strategy-proposals/${encodeURIComponent(proposalId)}/reject`,
      }),
      transformResponse: (response) =>
        experienceStrategyProposalStatusResponseSchema.parse(response),
    }),
  }),
});

export const {
  useConfirmExperienceStrategyProposalMutation,
  useGetConversationMessageWindowQuery,
  useGetConversationTurnSeekQuery,
  useGetExperienceStrategyCurrentQuery,
  useGetExperienceStrategyDetailQuery,
  useGetExperienceStrategyProposalQuery,
  useGetExperienceStrategyTimelineInfiniteQuery,
  useRejectExperienceStrategyProposalMutation,
} = experienceStrategyApi;
